import {
  Box,
  Flex,
  Group,
  GroupProps,
  Stack,
  StackProps,
  Text,
  ThemeIcon,
  Tooltip,
  useMantineColorScheme,
} from "@mantine/core";
import { useLocalStorage } from "@mantine/hooks";

import { NumberText } from "../NumberText";
import { TextSerif } from "../TextSerif";

import { IconInfo, IconMinus, IconPlus } from "@/components/icons";
import { LabelContentLocker } from "@/components/widgets/ContentLocker/LabelContentLocker";
import { getLimitedLabels } from "@/config/plans.config";
import { defaultCurrency } from "@/lib/currency/getCurrency";
import { usePlansUser } from "@/services/plans/get-user-plan";
import { Currency } from "@/types/currency";
import { formatCurrency } from "@/utils/formatEuro";

import type { MantineSize, MantineStyleProps, NumberFormatterProps } from "@mantine/core";
import type { FC, ReactNode } from "react";

const getLabelSize = (size: MantineSize): MantineSize => {
  switch (size) {
    case "xl":
      return "md";
    case "lg":
      return "sm";
    case "md":
      return "xs";
    case "sm":
      return "sm";
    default:
      return "xs";
  }
};

export type LabelValueProps = {
  titleTooltip?: string;
  label: ReactNode;
  labelSize?: MantineSize;
  size?: MantineSize;
  suffix?: string;
  value: ReactNode;
  valueProps?: NumberFormatterProps;
  valueSize?: MantineSize;
  valueType?: "currency";
  withChange?: "positive" | "negative";
  horizontal?: boolean;
  infoIcon?: ReactNode;
  truncate?: boolean;
} & MantineStyleProps;

export const LabelValue: FC<LabelValueProps> = ({
  titleTooltip,
  label,
  labelSize,
  size = "lg",
  suffix,
  value,
  valueProps,
  valueSize,
  valueType,
  withChange,
  horizontal,
  infoIcon,
  truncate = true,
  ...props
}) => {
  const { data: plans } = usePlansUser();

  /* Theme */
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === "dark";
  const redColor = isDark ? "danger" : "var(--mantine-color-danger-7)";
  const greenColor = isDark ? "success" : "var(--mantine-color-success-7)";
  const fontSize = { base: "sm", sm: "md" };
  const textColor = isDark ? "#c9c9c9" : "dimmed";

  const [currency] = useLocalStorage<Currency>({
    key: "currency",
    defaultValue: defaultCurrency,
  });

  const c =
    withChange === "negative" ? redColor : withChange === "positive" ? greenColor : undefined;
  const effectiveLabelSize = labelSize || getLabelSize(size);
  const effectiveValueSize = valueSize || size;

  const effectiveValueProps =
    valueType === "currency"
      ? {
          ...valueProps,
          suffix: suffix || currency.symbol, // Use currency symbol only if valueType is 'currency'
          decimalScale: 2,
          thousandSeparator: ",",
        }
      : {
          ...valueProps,
          suffix: suffix, // Use only the provided suffix if valueType is not 'currency'
        };

  const WrappingComponent = horizontal ? Group : Stack;
  const wrappingComponentProps: GroupProps & StackProps = horizontal
    ? {
        justify: "space-between",
        align: "flex-start",
        wrap: "nowrap",
      }
    : { gap: 0 };

  const canAccess = plans?.config.garage_risk_kpis ?? false;
  const isLocked = typeof label === "string" && getLimitedLabels.includes(label) && !canAccess;

  const hasInfo = Boolean(infoIcon || titleTooltip) && !isLocked;

  return (
    <WrappingComponent {...props} {...wrappingComponentProps}>
      <Flex justify="start" gap="md">
        <TextSerif
          c={textColor}
          fz={props.fz || effectiveLabelSize}
          styles={{ root: { whiteSpace: "nowrap" } }}
        >
          {label}
        </TextSerif>

        {hasInfo && (
          <Tooltip
            events={{ hover: true, focus: true, touch: true }}
            label={
              <Text
                truncate
                /* c={textColor} */
                style={{ whiteSpace: "normal", wordBreak: "break-word", lineHeight: "1.2" }}
                fz="sm"
                p="md"
              >
                {typeof infoIcon === "string" ? infoIcon : titleTooltip}
              </Text>
            }
            position="top"
            offset={4}
            maw={300}
            openDelay={100}
            withinPortal
          >
            <Box
              component="span"
              style={{
                display: "inline-flex",
                alignItems: "center",
                verticalAlign: "middle",
                position: "relative",
                bottom: 1,
              }}
            >
              <IconInfo fill="var(--mantine-color-secondary-4)" />
            </Box>
          </Tooltip>
        )}
      </Flex>
      <Group
        align="baseline"
        gap="0"
        style={{
          overflow: "hidden",
        }}
      >
        {isLocked ? (
          <LabelContentLocker />
        ) : (
          <>
            {withChange === "negative" && (
              <ThemeIcon color={c} variant="transparent" size="xs">
                <IconMinus size={14} />
              </ThemeIcon>
            )}
            {withChange === "positive" && (
              <ThemeIcon color={c} variant="transparent" size="xs">
                <IconPlus size={14} />
              </ThemeIcon>
            )}
            {typeof value === "number" ? (
              valueType === "currency" ? (
                <Text c={c} size={effectiveValueSize} fz={fontSize}>
                  {formatCurrency(value, currency.code)}
                </Text>
              ) : (
                <NumberText
                  c={c}
                  size={effectiveValueSize}
                  {...effectiveValueProps}
                  value={value}
                />
              )
            ) : typeof value === "string" ? (
              <Text
                c={c}
                size={effectiveValueSize}
                ta={horizontal ? "right" : "left"}
                truncate={truncate ? "end" : undefined}
                fz={fontSize}
                maw={truncate ? 300 : undefined}
              >
                {value}
                {suffix}
              </Text>
            ) : (
              value
            )}
          </>
        )}
      </Group>
    </WrappingComponent>
  );
};
